/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import _get from 'lodash/get';
import _map from 'lodash/map';
import _concat from 'lodash/concat';

import auth from './auth-service';

import { fetchGraphQL, mutateGraphQL } from '../helpers/dataHelpers';
import {
  GET_COLLECTION,
  DELETE_COLLECTION,
  CREATE_UPDATE_COLLECTION,
} from '../graphql/collection.js';
import collectionsQuery from '../graphql/collections.gql';
import collectionsUpdateMutation from '../graphql/update-collection.gql';
import uploadFile from '../services/UploadFile';

const collectionsProvider = {
  getList: async (resource, params) => {
    return fetchGraphQL(collectionsQuery, resource, params);
  },

  getOne: async (resource, params) => {
    return getCollection(resource, params);
  },

  getMany: (resource, params) =>
    fetchGraphQL(collectionsQuery, resource, params),

  getManyReference: (resource, params) => Promise,

  create: async (resource, params) => {
    const collection = await createOrUpdate(params.data, params.previousData);
    return { data: collection };
  },

  update: async (resource, params) => {
    const collection = await createOrUpdate(params.data, params.previousData);
    return { data: collection };
  },

  updateMany: (resource, params) => Promise,

  delete: async (resource, params) => {
    return await deleteCollection(resource, params);
  },

  deleteMany: (resource, params) => Promise,

  orderResource: async (resource, params) => {
    return await moveResource(resource, params);
  },
};

async function getCollection(resource, params) {
  let collection;
  let assets = [];
  let page = 1;
  // get assets chunk until all the assets have been fetch
  while (true) {
    const paramsWithPagination = {
      ...params,
      pagination: { page },
    };

    collection = await fetchGraphQL(
      GET_COLLECTION,
      resource,
      paramsWithPagination
    );

    const collectionAssetsChunk = _get(collection, 'data.assets', {});
    assets = _concat(assets, collectionAssetsChunk.items);

    const hasMore = _get(collection, 'data.assets.hasMore', false);
    if (!hasMore) {
      break;
    }

    ++page;
  }

  // rebuild collection with new assets list
  collection.data.assets.items = assets;

  return collection;
}

async function deleteCollection(resource, params) {
  await fetchGraphQL(DELETE_COLLECTION, resource, params);
  return { data: { id: params.id } };
}

async function moveResource(resource, params) {
  try {
    const { item, weight } = params;

    const { data: collection } = await collectionsProvider.getOne(resource, {
      id: item.id,
    });

    const { updatedCollection } = await mutateGraphQL(
      collectionsUpdateMutation,
      {
        id: item.id,
        collection: {
          title: collection.title,
          description: collection.description,
          tags: collection.tags,
          blogSummary: collection.blogSummary,
          blogLink: collection.blogLink,
          status: collection.status,
          weight: weight,
          assetIds: _map(collection.assets.items, (a) => a.id),
        },
      }
    );

    return { data: updatedCollection };
  } catch (e) {
    throw new Error(e);
  }
}

async function createOrUpdate(collection, previousCollection) {
  const newImage = _get(collection, 'imageUrl.rawFile', null);

  let thumbnailFileId;
  if (newImage) {
    const accessToken = await auth.getAccessToken();
    const res = await uploadFile(newImage, accessToken);
    if (!res.ok) {
      throw new Error('Image upload failed');
    }

    thumbnailFileId = res.body.id;
  }

  let params = {
    title: collection.title,
    status: collection.status,
    ...(collection.assets?.items.length > 0 && {
      assetIds: _map(collection.assets?.items, (a) => a.id),
    }),
    description: collection.description,
    tags: collection.tags,
    weight: collection.weight,
    blogSummary: collection.blogSummary,
    blogLink: collection.blogLink,
    ...(thumbnailFileId && { thumbnailFileId }),
  };

  const updateOrCreateResult = await mutateGraphQL(
    CREATE_UPDATE_COLLECTION,
    {
      collection: params,
      ...(collection.id && { id: collection.id }),
    },
    'createOrUpdateCollection'
  );

  // reading after creation or update (easier way to get all the assets back from the collection)
  const getResult = await getCollection('collections', {
    id: updateOrCreateResult.data.id,
  });
  return { ...getResult.data };
}

export default collectionsProvider;
